// Comparativa anual: año natural completo (1 ene – 31 dic) contra el anterior.
// Es presentacional: los datos los pide AnalyticsPage con los mismos filtros
// (zonas, canales, propiedades, activas, familiares, imputación) que el resto
// de la página. El rango es siempre el año natural, no el periodo seleccionado.

import { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, Loader2, GitCompareArrows } from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import type { CompareResult } from '@/lib/types';
import { classNames } from '@/lib/utils';
import { KPI_INFO } from '@/lib/kpiInfo';
import InfoTooltip from '@/components/ui/InfoTooltip';
import { useIsDarkMode } from '@/hooks/useIsDarkMode';

const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const KPI_ROWS: { key: string; format: (v: number) => string }[] = [
  { key: 'facturacion', format: fmtMoney },
  { key: 'neto', format: fmtMoney },
  { key: 'reservas', format: (v) => String(Math.round(v)) },
  { key: 'noches', format: (v) => String(Math.round(v)) },
  { key: 'huespedes', format: (v) => String(Math.round(v)) },
  { key: 'adr', format: fmtMoney },
  { key: 'estanciaMedia', format: (v) => `${Math.round(v)} noches` },
  { key: 'ocupacion', format: (v) => `${Math.round(v)}%` },
  { key: 'comision', format: fmtMoney },
];

interface Props {
  data: CompareResult | null;
  loading: boolean;
  year: number;
}

export default function YearComparison({ data, loading, year }: Props) {
  const isDark = useIsDarkMode();
  const chart = {
    grid: isDark ? '#ffffff14' : '#0B1D2E15',
    axis: isDark ? '#9ca3af' : '#0B1D2E99',
    barA: isDark ? '#D1E0F0' : '#0B1D2E',
    barB: isDark ? '#4A7BB0' : '#A3C2E0',
    cursor: isDark ? '#ffffff10' : '#0B1D2E08',
  };

  const prevYear = year - 1;

  // Mes a mes: los 12 meses siempre presentes, aunque un año no tenga datos
  const monthChart = useMemo(() => {
    const cur = new Map((data?.current.byMonth || []).map((m) => [m.month, m.facturacion]));
    const prev = new Map((data?.previous.byMonth || []).map((m) => [m.month, m.facturacion]));
    return MONTH_SHORT.map((name, i) => {
      const mm = String(i + 1).padStart(2, '0');
      return {
        name,
        actual: Math.round(cur.get(`${year}-${mm}`) || 0),
        anterior: Math.round(prev.get(`${prevYear}-${mm}`) || 0),
      };
    });
  }, [data, year, prevYear]);

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider flex items-center gap-2">
          <GitCompareArrows size={16} />
          Comparativa anual — {year} vs {prevYear}
        </h2>
        <span className="text-xs text-sereno-dark/40 flex items-center gap-1.5">
          {loading && <Loader2 size={14} className="animate-spin" />}
          Años naturales completos (1 ene – 31 dic)
          <InfoTooltip text={`Compara el año natural completo ${year} (1 de enero a 31 de diciembre) contra ${prevYear} completo, con los filtros aplicados arriba. No depende del periodo seleccionado. Del año en curso se incluyen también las reservas ya confirmadas de meses futuros, así que la comparación es "año completo contra año completo", no acumulado hasta hoy.`} />
        </span>
      </div>

      {!data ? (
        <div className="py-10 text-center text-sereno-dark/40 text-sm">
          {loading ? 'Calculando comparativa anual…' : 'Sin datos para la comparativa anual.'}
        </div>
      ) : (
        <>
          {/* Titulares de facturación */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <HeadlineCard sub={`Año en curso`} label={String(year)} value={fmtMoney(data.current.facturacion)} highlight />
            <HeadlineCard sub="Año anterior" label={String(prevYear)} value={fmtMoney(data.previous.facturacion)} />
            <DeltaCard delta={data.deltas.facturacion} />
          </div>

          {/* Mes a mes */}
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthChart} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: chart.axis }} />
              <YAxis tick={{ fontSize: 12, fill: chart.axis }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v: any) => fmtMoney(Number(v))} cursor={{ fill: chart.cursor }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="actual" name={String(year)} fill={chart.barA} radius={[4, 4, 0, 0]} />
              <Bar dataKey="anterior" name={String(prevYear)} fill={chart.barB} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          {/* KPIs */}
          <div className="overflow-x-auto mt-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-sereno-dark/50 border-b border-sereno-dark/10">
                  <th className="py-2 font-medium">KPI</th>
                  <th className="py-2 font-medium text-right">{year}</th>
                  <th className="py-2 font-medium text-right">{prevYear}</th>
                  <th className="py-2 font-medium text-right">Variación</th>
                </tr>
              </thead>
              <tbody>
                {KPI_ROWS.map((row) => {
                  const info = KPI_INFO[row.key];
                  return (
                    <tr key={row.key} className="border-b border-sereno-dark/5 last:border-0">
                      <td className="py-2.5">
                        <span className="inline-flex items-center gap-1.5">
                          {info?.label || row.key}
                          {info && <InfoTooltip text={info.info} />}
                        </span>
                      </td>
                      <td className="py-2.5 text-right font-semibold">{row.format((data.current as any)[row.key] as number)}</td>
                      <td className="py-2.5 text-right text-sereno-dark/60">{row.format((data.previous as any)[row.key] as number)}</td>
                      <td className="py-2.5 text-right"><DeltaBadge delta={data.deltas[row.key]} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function HeadlineCard({ label, value, sub, highlight }: { label: string; value: string; sub: string; highlight?: boolean }) {
  return (
    <div className={classNames('rounded-lg border border-sereno-dark/10 p-4', highlight && 'ring-2 ring-sereno-dark/10')}>
      <p className="text-xs text-sereno-dark/40 mb-1">{sub}</p>
      <p className="text-sm font-medium text-sereno-dark/70 mb-1">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function DeltaCard({ delta }: { delta: { abs: number; pct: number | null } }) {
  if (!delta) return null;
  const positive = delta.abs >= 0;
  const Icon = delta.abs === 0 ? Minus : positive ? TrendingUp : TrendingDown;
  const color = delta.abs === 0 ? 'text-sereno-dark/50' : positive ? 'text-emerald-600' : 'text-rose-600';
  return (
    <div className="rounded-lg border border-sereno-dark/10 p-4 flex flex-col justify-center">
      <p className="text-xs text-sereno-dark/40 mb-1">Variación facturación</p>
      <div className={classNames('flex items-center gap-2', color)}>
        <Icon size={24} />
        <span className="text-2xl font-bold">
          {delta.pct === null ? '—' : `${positive ? '+' : ''}${Math.round(delta.pct)}%`}
        </span>
      </div>
      <p className={classNames('text-sm font-medium mt-1', color)}>
        {positive ? '+' : ''}{fmtMoney(delta.abs)}
      </p>
    </div>
  );
}

function DeltaBadge({ delta }: { delta: { abs: number; pct: number | null } }) {
  if (!delta) return <span className="text-sereno-dark/30">—</span>;
  const positive = delta.abs >= 0;
  const Icon = delta.abs === 0 ? Minus : positive ? TrendingUp : TrendingDown;
  const color = delta.abs === 0 ? 'text-sereno-dark/40' : positive ? 'text-emerald-600' : 'text-rose-600';
  return (
    <span className={classNames('inline-flex items-center gap-1 font-semibold', color)}>
      <Icon size={14} />
      {delta.pct === null ? 'n/a' : `${positive ? '+' : ''}${Math.round(delta.pct)}%`}
    </span>
  );
}
